import { apiClient } from "@/shared/lib/axios";

/** Params for admin massager list (RA2 はり・きゅう師CSV). */
export interface MassagerListParams {
  clinic_number?: string;
  clinic_name?: string;
  /** Super-admin only: filter by clinic site type. */
  site_types?: string[];
  search_login_id?: string;
  search_name?: string;
  sort?: [string, "asc" | "desc"];
  page?: number;
  per_page?: number;
}

/** One row in admin massager list. */
export interface MassagerListItem {
  id: number;
  clinic_number: string;
  /** From clinics.site_type. Shown as サイト区分 for super-admin. */
  site_type?: string | null;
  clinic_name: string;
  name_kana?: string | null;
  login_id?: string | null;
  serial_number: number | string;
  massager_name: string;
  /** R2 | A2 from massagers.service_type */
  service_type?: string | null;
  license_number?: string | null;
  acupuncture_license_number?: string | null;
  moxibustion_license_number?: string | null;
}

export interface MassagerListResponse {
  success: boolean;
  data: MassagerListItem[];
  meta?: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
  message?: string;
}

export const getMassagers = async (
  site: string,
  params: MassagerListParams
): Promise<MassagerListResponse> => {
  const response = await apiClient.get<MassagerListResponse>(
    `/admin/${site}/massagers`,
    { params }
  );
  return response.data;
};

/** One clinic location in create/update payload (id + 適用期間 + 登録日・廃止依頼日). */
export interface ClinicLocationWithAvailable {
  id: number;
  available_from?: string | null;
  available_to?: string | null;
  regist_date?: string | null;
  stop_request_date?: string | null;
}

export interface CreateMassagerPayload {
  clinic_id: number;
  clinic_location_ids_a2?: ClinicLocationWithAvailable[];
  clinic_location_ids_r2?: ClinicLocationWithAvailable[];
  /** A2 | R2 | BOTH: FE decides which side is active for validation/persistence. */
  service_type?: 'A2' | 'R2' | 'BOTH' | null;
  name: string;
  name_kana?: string | null;
  login_id?: string | null;
  password?: string | null;
  email?: string | null;
  acupuncture_license_number?: string | null;
  moxibustion_license_number?: string | null;
  /** R2: 免許番号 — API key is massage_license_number */
  massage_license_number?: string | null;
  /** @deprecated Use massage_license_number for API; form uses license_number */
  license_number?: string | null;
  wide_area_union_code?: string | null;
  wide_area_union_number?: string | null;
  prefecture_number?: string | null;
  wide_area_union_rows?: WideAreaUnionRow[] | null;
  /** R2用（BOTH 時はマッサージ側の保険者別番号） */
  massage_wide_area_union_rows?: WideAreaUnionRow[] | null;
}

export interface WideAreaUnionRow {
  wide_area_union_code?: string | null;
  wide_area_union_number?: string | null;
  prefecture_number?: string | null;
  /** A2 | R2 (from massager_wide_area_union_numbers.service_type) */
  service_type?: string | null;
}

/** Payload for update (no clinic_id; password optional, empty = do not change). */
export interface UpdateMassagerPayload {
  clinic_location_ids_a2?: ClinicLocationWithAvailable[];
  clinic_location_ids_r2?: ClinicLocationWithAvailable[];
  /** A2 | R2 | BOTH: FE decides which side is active for validation/persistence. */
  service_type?: 'A2' | 'R2' | 'BOTH' | null;
  name: string;
  name_kana?: string | null;
  login_id?: string | null;
  password?: string | null;
  email?: string | null;
  acupuncture_license_number?: string | null;
  moxibustion_license_number?: string | null;
  /** R2: 免許番号 — API key is massage_license_number */
  massage_license_number?: string | null;
  license_number?: string | null;
  wide_area_union_code?: string | null;
  wide_area_union_number?: string | null;
  prefecture_number?: string | null;
  wide_area_union_rows?: WideAreaUnionRow[] | null;
  massage_wide_area_union_rows?: WideAreaUnionRow[] | null;
}

export interface CreateMassagerResponse {
  success: boolean;
  message?: string;
  data?: {
    id: number;
    serial_number: number;
    name: string;
  };
}

/** Massager detail for edit form (API show response data). */
/** One clinic location in API response (id + 適用期間 + 登録日・廃止依頼日). */
export interface MassagerDetailClinicLocation {
  id: number;
  available_from?: string | null;
  available_to?: string | null;
  regist_date?: string | null;
  stop_request_date?: string | null;
}

export interface MassagerDetail {
  id: number;
  serial_number: number;
  /** A2 | R2 — form layout and which fields to show/save */
  service_type?: string | null;
  clinic_id: number;
  clinic_location_id: number | null;
  clinic_location_ids_a2?: (number | MassagerDetailClinicLocation)[];
  clinic_location_ids_r2?: (number | MassagerDetailClinicLocation)[];
  name: string;
  name_kana?: string | null;
  login_id?: string | null;
  password?: string | null;
  email?: string | null;
  fax?: string | null;
  rezept_address?: string | null;
  base_name?: string | null;
  acupuncture_license_number?: string | null;
  moxibustion_license_number?: string | null;
  license_number?: string | null;
  health_center_regist_type_id?: number | null;
  wide_area_union_code?: string | null;
  wide_area_union_number?: string | null;
  prefecture_number?: string | null;
  wide_area_union_rows?: WideAreaUnionRow[] | null;
  massage_wide_area_union_rows?: WideAreaUnionRow[] | null;
  /** Max visit date (Y-m-d) across all visits for this massager. Used for available_to validation. */
  last_visit_date?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
}

export const createMassager = async (
  site: string,
  data: CreateMassagerPayload
): Promise<CreateMassagerResponse> => {
  const response = await apiClient.post<CreateMassagerResponse>(
    `/admin/${site}/massagers`,
    data
  );
  return response.data;
};

export const getMassager = async (
  site: string,
  id: number
): Promise<{ success: boolean; data?: MassagerDetail }> => {
  const response = await apiClient.get<{ success: boolean; data?: MassagerDetail }>(
    `/admin/${site}/massagers/${id}`
  );
  return response.data;
};

export const updateMassager = async (
  site: string,
  id: number,
  data: UpdateMassagerPayload
): Promise<{ success: boolean; message?: string; data?: MassagerDetail }> => {
  const response = await apiClient.put<{
    success: boolean;
    message?: string;
    data?: MassagerDetail;
  }>(`/admin/${site}/massagers/${id}`, data);
  return response.data;
};
